import { useEffect, useState, useCallback } from "react";
import { friendsApi } from "@/lib/apiClient";
import { Users, UsersThree, UserPlus, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

// Global event name used to broadcast status changes between button instances
const FRIEND_STATUS_EVENT = "friend_status_change";

export function dispatchFriendStatusChange(handle) {
    window.dispatchEvent(new CustomEvent(FRIEND_STATUS_EVENT, { detail: { handle } }));
}

export default function FriendButton({ handle, size = "sm", className = "", hideIfNotPending = false }) {
    const { t } = useTranslation();
    const { user } = useAuth() || {};
    const [status, setStatus] = useState("loading"); // "none", "requested", "pending_accept", "friends"
    const [busy, setBusy] = useState(false);

    const fetchStatus = useCallback(() => {
        if (!user || !handle) return;
        friendsApi.status(handle)
            .then((r) => setStatus(r.status))
            .catch(() => setStatus("none"));
    }, [user, handle]);

    // Initial load
    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    // Re-fetch when another FriendButton dispatches a change for this handle,
    // or when the user switches back to this browser tab
    useEffect(() => {
        const onStatusChange = (e) => {
            if (e.detail?.handle === handle) fetchStatus();
        };
        const onVisibility = () => {
            if (document.visibilityState === "visible") fetchStatus();
        };
        window.addEventListener(FRIEND_STATUS_EVENT, onStatusChange);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener(FRIEND_STATUS_EVENT, onStatusChange);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [handle, fetchStatus]);

    if (!user || !handle || user.handle === handle) return null;
    if (status === "loading") return null;
    if (hideIfNotPending && status !== "pending_accept") return null;

    const action = async (e) => {
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        try {
            if (status === "none") {
                await friendsApi.request(handle);
                setStatus("requested");
                toast.success("Friend request sent.");
            } else if (status === "friends") {
                if (window.confirm("Remove friend?")) {
                    await friendsApi.remove(handle);
                    setStatus("none");
                    toast.success("Friend removed.");
                }
            } else if (status === "pending_accept") {
                await friendsApi.accept(handle);
                setStatus("friends");
                toast.success("Friend request accepted.");
            } else if (status === "requested") {
                await friendsApi.remove(handle); // Cancel request
                setStatus("none");
                toast.success("Friend request cancelled.");
            }
            // Notify all other FriendButton instances with the same handle to re-sync
            dispatchFriendStatusChange(handle);
        } catch (err) {
            toast.error("An error occurred.");
        } finally {
            setBusy(false);
        }
    };

    const small = size === "sm";

    let label = "Add Friend";
    let icon = <UserPlus size={12} weight="bold" />;
    let bg = "var(--gold)";
    let color = "var(--navy)";
    let border = "1px solid var(--gold)";

    if (status === "friends") {
        label = "Friends";
        icon = <Users size={12} weight="bold" />;
        bg = "transparent";
        color = "white";
        border = "1px solid rgba(255,255,255,0.2)";
    } else if (status === "requested") {
        label = "Requested";
        icon = <CheckCircle size={12} weight="bold" />;
        bg = "transparent";
        color = "rgba(255,255,255,0.6)";
        border = "1px solid rgba(255,255,255,0.2)";
    } else if (status === "pending_accept") {
        label = "Accept Request";
        icon = <UsersThree size={12} weight="bold" />;
        bg = "#10b981"; // Emerald green
        color = "white";
        border = "1px solid #10b981";
    }

    return (
        <button
            onClick={action}
            disabled={busy}
            className={`inline-flex items-center gap-1.5 rounded-full transition-colors duration-300 ${className}`}
            style={{
                padding: small ? "5px 12px" : "9px 18px",
                fontSize: small ? 11 : 13,
                background: bg,
                color: color,
                border: border,
                fontWeight: 500,
            }}
            data-testid={`friend-btn-${handle}`}
        >
            {icon}
            {label}
        </button>
    );
}
